import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  Typography,
  Divider
} from '@mui/material';
import {
  Dashboard,
  Analytics,
  Create,
  CalendarToday,
  Settings,
  Facebook,
  Instagram,
  YouTube,
  LinkedIn,
  Psychology,
  SmartToy,
  School,
  MyLocation,
  People,
  AutoAwesome,
  Schedule,
  TrendingUp,
  CloudUpload,
  Campaign
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

  const drawerWidth = { xs: 280, sm: 200 };

const menuItems = [
  { text: 'Dashboard', icon: <Dashboard />, path: '/' },
  { text: 'Analytics', icon: <Analytics />, path: '/analytics' },
  { text: 'Content', icon: <Create />, path: '/content' },
  { text: 'Calendar', icon: <CalendarToday />, path: '/calendar' },
  { text: 'Lead Generation', icon: <MyLocation />, path: '/lead-capture' },
  { text: 'Lead Intelligence Hub', icon: <Psychology />, path: '/unified-leads' },
  { text: 'Smart Follow-up', icon: <Schedule />, path: '/smart-follow-up' },
  { text: 'Revenue Intelligence', icon: <TrendingUp />, path: '/revenue-intelligence' },
  { text: 'Import CSV', icon: <CloudUpload />, path: '/csv-import' },
  { text: 'AI Insights', icon: <Psychology />, path: '/ai-insights' },
  { text: 'AI Agent', icon: <SmartToy />, path: '/ai-agent' },
  { text: 'Training', icon: <School />, path: '/training' },
  { text: 'Settings', icon: <Settings />, path: '/settings' },
];

const socialPlatforms = [
  { text: 'Facebook', icon: <Facebook />, color: '#1877f2' },
  { text: 'Instagram', icon: <Instagram />, color: '#e4405f' },
  { text: 'YouTube', icon: <YouTube />, color: '#ff0000' },
  { text: 'TikTok', icon: <Instagram />, color: '#000000' },
  { text: 'LinkedIn', icon: <LinkedIn />, color: '#0077b5' },
];

const Sidebar = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <Drawer
      variant="persistent"
      anchor="left"
      open={open}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        display: { xs: open ? 'block' : 'none', sm: 'block' },
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: { xs: '56px', sm: '64px' }, // Responsive header height
          height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' }, // Responsive height
          overflow: 'hidden', // Prevent double scrollbars
          border: 'none',
          backgroundColor: 'var(--background-card)',
          boxShadow: 'var(--shadow-md)',
          borderRight: '1px solid var(--border-color)'
        },
      }}
    >
              <Box sx={{ 
          overflow: 'auto', 
          height: '100%',
          backgroundColor: 'var(--background-card)',
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'var(--border-color)',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--text-muted)',
            borderRadius: '2px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--text-secondary)',
          },
        }}>
        <Box sx={{ p: 2, borderBottom: '1px solid var(--border-color)' }}>
          <Typography variant="h6" sx={{ 
            color: 'var(--primary-color)', 
            fontWeight: 600,
            fontSize: '18px'
          }}>
            Social Media Tool
          </Typography>
        </Box>
        
        <Divider sx={{ borderColor: 'var(--border-color)', my: 1 }} />
        
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: '6px',
                  mb: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'var(--primary-color)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'var(--primary-dark)',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    }
                  },
                  '&:hover': {
                    backgroundColor: 'var(--background-elevated)',
                  }
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  color: location.pathname === item.path ? 'white' : 'var(--text-secondary)'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  sx={{ 
                    '& .MuiTypography-root': {
                      fontSize: '14px',
                      fontWeight: location.pathname === item.path ? 600 : 400
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        
        <Divider sx={{ borderColor: 'var(--border-color)', my: 1 }} />
        
        <Box sx={{ p: 2, pt: 3 }}>
          <Typography variant="subtitle2" sx={{ 
            color: 'var(--text-secondary)', 
            fontWeight: 500,
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            Connected Platforms
          </Typography>
        </Box>
        
        <List>
          {socialPlatforms.map((platform) => (
            <ListItem key={platform.text} disablePadding>
              <ListItemButton sx={{
                mx: 1,
                borderRadius: '6px',
                mb: 0.5,
                '&:hover': {
                  backgroundColor: 'var(--background-elevated)',
                }
              }}>
                <ListItemIcon sx={{ 
                  minWidth: 40,
                  color: platform.color 
                }}>
                  {platform.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={platform.text} 
                  sx={{ 
                    '& .MuiTypography-root': {
                      fontSize: '14px',
                      color: 'var(--text-secondary)'
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
